import React, { useEffect, useState, useCallback } from 'react';
import { ScrollText, Plus, Pencil, Trash2, MessageCircle, MessageSquare, Clock, Rocket, Flag, CheckCheck, ShieldCheck, Zap, CheckCircle2 } from 'lucide-react';
import { CommanServices } from '@/services/commanServices';

const ACTION_META = {
  Reported: { icon: Flag, color: 'text-blue-600', bg: 'bg-blue-50', dot: 'bg-blue-500' },
  Added: { icon: Plus, color: 'text-emerald-600', bg: 'bg-emerald-50', dot: 'bg-emerald-500' },
  Updated: { icon: Pencil, color: 'text-amber-600', bg: 'bg-amber-50', dot: 'bg-amber-500' },
  Removed: { icon: Trash2, color: 'text-rose-600', bg: 'bg-rose-50', dot: 'bg-rose-500' },
  Acknowledged: { icon: CheckCheck, color: 'text-sky-600', bg: 'bg-sky-50', dot: 'bg-sky-500' },
  Verified: { icon: ShieldCheck, color: 'text-teal-600', bg: 'bg-teal-50', dot: 'bg-teal-500' },
  Performed: { icon: Zap, color: 'text-violet-600', bg: 'bg-violet-50', dot: 'bg-violet-500' },
  Fired: { icon: Zap, color: 'text-orange-600', bg: 'bg-orange-50', dot: 'bg-orange-500' },
  // Response-plan activation — highlighted so it's unmissable in the trail.
  Activated: { icon: Rocket, color: 'text-indigo-600', bg: 'bg-indigo-50', dot: 'bg-indigo-500' },
  Resolved: { icon: CheckCircle2, color: 'text-emerald-700', bg: 'bg-emerald-50', dot: 'bg-emerald-600' },
};
const DEFAULT_ACTION = { icon: ScrollText, color: 'text-gray-600', bg: 'bg-gray-50', dot: 'bg-gray-400' };

const fmt = (ts) => {
  if (!ts) return '';
  const d = new Date(ts);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleString([], { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });
};

const formatAuditDetail = (value) => {
  if (value == null) return '';
  if (typeof value !== 'object') return String(value);

  const summary = [
    value.title,
    value.status ? `Status: ${value.status}` : '',
  ].filter(Boolean).join(' · ');

  if (summary) return summary;

  try {
    return JSON.stringify(value);
  } catch (_) {
    return 'Audit details unavailable';
  }
};

export default function AuditLogWidget({ incident }) {
  const incidentId = incident?.id;
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    if (!incidentId) return;
    try {
      const [auditRes, feedRes] = await Promise.all([
        CommanServices.getIncidentAuditLog(incidentId),
        CommanServices.getIncidentActivityFeed(incidentId),
      ]);
      const auditData = auditRes?.data ?? auditRes ?? {};
      const audits = (auditData.entries || []).map((e) => ({
        kind: 'audit',
        action: e.action,
        text: formatAuditDetail(e.detail),
        actor: e.actor?.name || 'Admin',
        created_at: e.created_at,
        id: e.id,
      }));
      const feedData = feedRes?.data ?? feedRes ?? {};
      const feed = (feedData.feed || []).map((f) => ({
        kind: f.type === 'message' ? 'message' : 'comment',
        action: f.type === 'message' ? 'Chat' : 'Comment',
        text: f.type === 'comment' ? `${f.card_name}: ${f.message}` : f.message,
        actor: f.author || 'User',
        created_at: f.created_at,
        id: `${f.type}-${f.id}`,
      }));
      const merged = [...audits, ...feed].sort(
        (a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0)
      );
      setEntries(merged);
    } catch (e) {
      setEntries([]);
    } finally {
      setLoading(false);
    }
  }, [incidentId]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const h = () => load();
    window.addEventListener('timelineRefresh', h);
    window.addEventListener('refresh-activity-feed', h);
    window.addEventListener('incidentRealtimeSync', h);
    return () => {
      window.removeEventListener('timelineRefresh', h);
      window.removeEventListener('refresh-activity-feed', h);
      window.removeEventListener('incidentRealtimeSync', h);
    };
  }, [load]);

  const iconFor = (entry) => {
    if (entry.kind === 'comment') return { icon: MessageCircle, color: 'text-sky-600', bg: 'bg-sky-50', dot: 'bg-sky-500' };
    if (entry.kind === 'message') return { icon: MessageSquare, color: 'text-indigo-600', bg: 'bg-indigo-50', dot: 'bg-indigo-500' };
    return ACTION_META[entry.action] || DEFAULT_ACTION;
  };

  return (
    <div className="bg-white overflow-hidden" data-testid="audit-log-widget">
      <div className="flex items-center justify-between px-5 py-3.5 border-b bg-slate-50/60">
        <div className="flex items-center gap-2.5">
          <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-slate-800 text-white">
            <ScrollText className="h-4 w-4" />
          </span>
          <div>
            <h3 className="text-sm font-bold text-gray-900 leading-tight">Audit Log</h3>
            <p className="text-[11px] text-gray-500">Every change, comment & message on this {incident?.is_scenario ? 'scenario' : incident?.is_exercise ? 'drill' : 'incident'}</p>
          </div>
        </div>
        <span className="text-[10px] font-bold text-slate-600 bg-slate-100 border border-slate-200 rounded-full px-2 py-0.5" data-testid="audit-log-count">
          {entries.length}
        </span>
      </div>

      {loading ? (
        <div className="text-center py-8 text-sm text-gray-400" data-testid="audit-log-loading">Loading audit trail…</div>
      ) : entries.length === 0 ? (
        <div className="text-center py-8" data-testid="audit-log-empty">
          <Clock className="h-8 w-8 text-gray-300 mx-auto mb-2" />
          <p className="text-sm text-gray-500 font-medium">No activity yet</p>
          <p className="text-xs text-gray-400 mt-1">Actions, comments and messages will appear here.</p>
        </div>
      ) : (
        <div className="max-h-[420px] overflow-y-auto divide-y divide-gray-100">
          {entries.map((e) => {
            const meta = iconFor(e);
            const Icon = meta.icon;
            return (
              <div key={e.id} className="flex items-start gap-3 px-5 py-3 hover:bg-slate-50/60" data-testid={`audit-log-entry-${e.id}`}>
                <span className={`mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-lg ${meta.bg}`}>
                  <Icon className={`h-3.5 w-3.5 ${meta.color}`} />
                </span>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <span className={`text-[10px] font-bold uppercase tracking-wide ${meta.color}`}>{e.action}</span>
                    <span className="text-[11px] text-gray-400">{fmt(e.created_at)}</span>
                  </div>
                  <p className="text-sm text-gray-700 leading-snug break-words">{e.text}</p>
                  <p className="text-[11px] text-gray-400 mt-0.5">by {e.actor}</p>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
